import React, { useState, ChangeEvent } from 'react';
import { View, Pressable, TextInput } from 'react-native-web';
import { useNavigate } from 'react-router-dom';
import CustomText from '../../components/atoms/CustomText';
import { colors, spacing, typography } from '../../utils/styling';
import { useAccounts, type Account } from '../../lib/salesforce';
import SvgSearch from '../../components/icons/SvgSearch';
import { useUser, useLogout } from '../../lib/auth';
import { useCurrentProfile } from '../../lib/profiles';
import { useResponsive } from '../../hooks/useResponsive';
import { useLeadsDetailed, type Lead, type LeadDetailed } from '../../lib/leads';
import { useQueryClient } from '@tanstack/react-query';
import { useSubscriptions, type Subscription, isSubscriptionActive, getSubscriptionStatus } from '../../lib/subscriptions';
import LeadQuizResultsModal from '../../components/popups/LeadQuizResultsModal';
import { clearMoneyPersonalityAssessmentState } from '../../utils/quizRetake';

// Mock data fields to supplement Salesforce data
const MOCK_DATA = {
  riskScore: [51, 65, 42, 38, 73, 45],
  status: ['ACTIVE', 'INACTIVE'],
  totalAssets: ['$1,027,622', '$2,543,100', '$847,250', '$1,523,450', '$932,100', '$3,245,800'],
  email: '@stratoswp.com',
  phone: '(555) 123-'
};

// The five main Money Personality archetypes (for the archetype filter).
const ARCHETYPES = [
  'Dream Chaser',
  'Wealth Architect',
  'Safety Seeker',
  'Generous Altruist',
  'Freedom Finder',
];

// Demographic columns surfaced in the admin lead report. `keys` are the exact
// question texts persisted in quizzes.info, first match wins: the main quiz's
// profile questions (utils/quizinfo/questions.ts, saved since 2026-09-23) and
// the older InfoCollection form (utils/infoCollectionQuestions.tsx).
// These are the lead-quality fields Nina asked for (age, net worth, income).
// The quiz gained an income question 2026-10-05; Income fills for quizzes taken
// from then on, plus older InfoCollection submissions.
const DEMOGRAPHIC_COLUMNS: { key: string; keys: string[]; label: string }[] = [
  { key: 'age', keys: ['What is your age group?', 'How old are you?'], label: 'Age' },
  { key: 'net_worth', keys: ['What is your approximate net worth?', 'Roughly how much do you own in assets?'], label: 'Net Worth' },
  { key: 'income', keys: ['What is your approximate annual household income?', 'What is your household annual income?'], label: 'Income' },
];

// Read a demographic answer off an enriched lead's info map.
const infoVal = (lead: LeadDetailed, key: string): string => {
  const col = DEMOGRAPHIC_COLUMNS.find(c => c.key === key);
  const info = lead.info || {};
  for (const k of col ? col.keys : [key]) {
    if (info[k]) return info[k];
  }
  return '';
};

// On a phone the ten-column tables squeeze every cell to a sliver and the
// details stop showing (Nina, 2026-10-07 call). Phones get one card per record
// with label/value rows instead; desktop keeps the table.
const MobileRecordCard = ({
  title,
  rows,
  onPress,
}: {
  title: string;
  rows: { label: string; value: string }[];
  onPress?: () => void;
}) => (
  <Pressable
    onPress={onPress}
    disabled={!onPress}
    style={{
      backgroundColor: colors.white,
      border: `1px solid ${colors.gray[200]}`,
      borderRadius: 8,
      padding: spacing.md,
      marginBottom: spacing.sm,
      cursor: onPress ? 'pointer' : 'default',
    }}
  >
    <CustomText style={{ fontSize: 17, fontWeight: '700', color: colors.dark, fontFamily: typography.fontFamily.body, marginBottom: 6 }}>
      {title}
    </CustomText>
    {rows.map(r => (
      <View key={r.label} style={{ flexDirection: 'row', paddingVertical: 3 }}>
        <CustomText style={{ width: 96, flexShrink: 0, fontSize: 14, color: colors.gray[600], fontFamily: typography.fontFamily.body }}>
          {r.label}
        </CustomText>
        <CustomText style={{ flex: 1, minWidth: 0, fontSize: 14, color: colors.dark, fontFamily: typography.fontFamily.body, overflowWrap: 'anywhere' } as any}>
          {r.value}
        </CustomText>
      </View>
    ))}
  </Pressable>
);

const formatLeadDate = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  });

// Native <select> styling (react-native-web has no native picker; a plain
// select keeps the filter accessible and avoids a new dependency).
const selectStyle: React.CSSProperties = {
  height: 36,
  borderRadius: 6,
  border: `1px solid ${colors.gray[300]}`,
  padding: '0 8px',
  fontFamily: typography.fontFamily.body,
  fontSize: 14,
  backgroundColor: colors.white,
  color: colors.dark,
  cursor: 'pointer',
};

// Add ClientInitials component before the main component
const ClientInitials = ({ name }: { name: string }) => {
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <View style={{
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.gray[200],
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: spacing.md,
    }}>
      <CustomText style={{
        color: colors.gray[700],
        fontSize: 14,
        fontWeight: '600',
        fontFamily: typography.fontFamily.body,
      }}>
        {initials}
      </CustomText>
    </View>
  );
};

export default function AdvisorPortal() {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { data: accountsData, isLoading, error } = useAccounts();
  const { data: user } = useUser();
  const { mutate: logout } = useLogout();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'clients' | 'admin'>('clients');
  const [adminView, setAdminView] = useState<'leads' | 'subscriptions'>('leads');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  // Lead-report filtering / sorting (MONEY-7)
  const [archetypeFilter, setArchetypeFilter] = useState<string>('');
  const [netWorthFilter, setNetWorthFilter] = useState<string>('');
  const [sortKey, setSortKey] = useState<'created_at' | 'name' | 'archetype'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  
  // Add CSS for spinner animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // Preview-as-fresh-user (MONEY 86babnkpf): the "already taken the quiz" block is
  // driven entirely by browser localStorage, so Nina/Halle can't re-walk the live
  // quiz -> unlock -> results -> PDF flow once they've taken it once. This wipes the
  // assessment + payment localStorage keys for THIS browser, then opens the live quiz
  // from the start in a new tab (keeping the admin session in the current tab).
  const handlePreviewAsFreshUser = () => {
    clearMoneyPersonalityAssessmentState();
    // Also clear the payment/coupon/subscription markers the gate reads so the
    // preview behaves as a never-paid, never-taken visitor.
    ['payment_success', 'mpay_coupon_redeemed', 'subscription_active', 'stripe_customer_id'].forEach(
      (key) => localStorage.removeItem(key)
    );
    window.open('/quiz', '_blank', 'noopener');
  };

  // Fetch leads (enriched with archetype + demographics) and subscriptions for admin tab
  const { data: leadsData, isLoading: leadsLoading, error: leadsError } = useLeadsDetailed(undefined, {
    enabled: activeTab === 'admin'
  });

  // Distinct net-worth tiers present in the data, for the filter dropdown.
  const netWorthOptions = React.useMemo(() => {
    const vals = new Set<string>();
    (leadsData || []).forEach((lead: LeadDetailed) => {
      const v = infoVal(lead, 'Roughly how much do you own in assets?');
      if (v) vals.add(v);
    });
    return Array.from(vals).sort();
  }, [leadsData]);

  // Apply filters + sort to the lead list for the report table + CSV export.
  const displayedLeads = React.useMemo(() => {
    let rows = [...((leadsData as LeadDetailed[]) || [])];
    if (archetypeFilter) rows = rows.filter(l => l.archetype === archetypeFilter);
    if (netWorthFilter) rows = rows.filter(l => infoVal(l, 'Roughly how much do you own in assets?') === netWorthFilter);
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'created_at') {
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortKey === 'name') {
        cmp = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
      } else if (sortKey === 'archetype') {
        cmp = (a.archetype || '').localeCompare(b.archetype || '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [leadsData, archetypeFilter, netWorthFilter, sortKey, sortDir]);

  // Toggle sort on a column header click.
  const toggleSort = (key: 'created_at' | 'name' | 'archetype') => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  // Individual subscriptions span every firm, so only platform admins see them (2026-10-09).
  const { data: currentProfile } = useCurrentProfile();
  const isPlatformAdmin = currentProfile?.user_type === 'admin';
  const { data: subscriptionsData, isLoading: subscriptionsLoading, error: subscriptionsError } = useSubscriptions(undefined, { enabled: isPlatformAdmin });

  // Debug logging for subscriptions
  console.log('🔍 SUBSCRIPTIONS DEBUG:', {
    subscriptionsData,
    subscriptionsLoading,
    subscriptionsError,
    hasData: !!subscriptionsData,
    dataLength: subscriptionsData?.length || 0
  });

  // Function to generate consistent mock data for each account
  const getMockData = (index: number) => ({
    riskScore: MOCK_DATA.riskScore[index % MOCK_DATA.riskScore.length],
    status: MOCK_DATA.status[index % MOCK_DATA.status.length],
    totalAssets: MOCK_DATA.totalAssets[index % MOCK_DATA.totalAssets.length],
    email: `${index}${MOCK_DATA.email}`,
    phone: `${MOCK_DATA.phone}${String(index + 1000).slice(1)}`
  });

  // Function to download leads as CSV (includes archetype + demographic
  // lead-quality fields, and respects the active filters/sort — MONEY-7).
  const escapeCsv = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
  const downloadLeadsCSV = (leads: LeadDetailed[]) => {
    const headers = [
      'Name', 'Email', 'Phone', 'Archetype',
      ...DEMOGRAPHIC_COLUMNS.map(c => c.label),
      'SF Account', 'Quiz ID', 'Created',
    ];
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        escapeCsv(((lead.first_name || '') + ' ' + (lead.last_name || '')).trim() || 'N/A'),
        escapeCsv(lead.email || 'N/A'),
        escapeCsv(lead.phone || 'N/A'),
        escapeCsv(lead.archetype || 'N/A'),
        ...DEMOGRAPHIC_COLUMNS.map(c => escapeCsv(infoVal(lead, c.key) || 'N/A')),
        escapeCsv(lead.sf_account || 'N/A'),
        escapeCsv(lead.quiz != null ? String(lead.quiz) : 'N/A'),
        escapeCsv(new Date(lead.created_at).toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }))
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter accounts based on search query
  const filteredAccounts = accountsData?.records
    .sort((a: Account, b: Account) => a.Name.localeCompare(b.Name))
    .filter((account: Account) => {
      const searchLower = searchQuery.toLowerCase();
      return account.Name.toLowerCase().includes(searchLower);
    });

  // Handle lead click
  const handleLeadClick = (lead: Lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowLeadModal(false);
    setSelectedLead(null);
  };

  // Handle lead deletion
  const handleLeadDelete = () => {
    // Refresh both leads and subscriptions data
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        {/* On a phone the header wraps: logo + tabs, then the buttons on their own
            rows. Unwrapped, the buttons sat past the right edge where nobody could
            tap them (Nina, 2026-10-07 call). */}
        <View style={isMobile
          ? { ...styles.headerContent, flexWrap: 'wrap', paddingHorizontal: spacing.md, gap: spacing.sm }
          : styles.headerContent}>
          {/* Logo */}
          <View style={styles.headerLeft}>
            <img
              src={require('../../components/img/money_personality_full_logo.png')}
              alt="Money Personality Logo"
              style={{ height: 48, width: 'auto', objectFit: 'contain' }}
            />
            {/* Navigation tabs */}
            <View style={{ flexDirection: 'row', marginLeft: isMobile ? spacing.sm : spacing.xl }}>
              <Pressable
                style={{
                  paddingVertical: 16,
                  paddingHorizontal: spacing.md,
                  position: 'relative',
                  marginRight: spacing.md,
                }}
                onPress={() => setActiveTab('clients')}
              >
                <CustomText
                  style={{
                    color: activeTab === 'clients' ? colors.primary : colors.gray[600],
                    fontWeight: '600',
                    fontSize: 14,
                    fontFamily: typography.fontFamily.body,
                  }}
                >
                  CLIENTS
                </CustomText>
                {activeTab === 'clients' && (
                  <View style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: colors.primary,
                  }} />
                )}
              </Pressable>
              
              <Pressable
                style={{
                  paddingVertical: 16,
                  paddingHorizontal: spacing.md,
                  position: 'relative',
                }}
                onPress={() => setActiveTab('admin')}
              >
                <CustomText
                  style={{
                    color: activeTab === 'admin' ? colors.primary : colors.gray[600],
                    fontWeight: '600',
                    fontSize: 14,
                    fontFamily: typography.fontFamily.body,
                  }}
                >
                  ADMIN
                </CustomText>
                {activeTab === 'admin' && (
                  <View style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: colors.primary,
                  }} />
                )}
              </Pressable>
            </View>
          </View>

          {/* User Email and Logout */}
          <View style={isMobile
            ? { ...styles.headerRight, flexWrap: 'wrap', width: '100%', rowGap: spacing.sm }
            : styles.headerRight}>
            {/* Lead routing config + accountability (86badfku3). */}
            <Pressable
              onPress={() => navigate('/advisor/routing')}
              style={({ hovered }: { hovered: boolean }) => ({
                marginRight: spacing.md,
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.md,
                backgroundColor: hovered ? colors.gray[100] : 'transparent',
                border: `1px solid ${colors.gray[300]}`,
                borderRadius: 6,
                cursor: 'pointer',
              })}
            >
              <CustomText style={{
                color: colors.gray[700],
                fontSize: isMobile ? 16 : 13,
                fontWeight: '600',
                fontFamily: typography.fontFamily.body,
              }}>
                Lead routing
              </CustomText>
            </Pressable>
            {/* Marketing kit: branding, quiz QR, social graphics (86bagxuwz). */}
            <Pressable
              onPress={() => navigate('/advisor/marketing')}
              style={({ hovered }: { hovered: boolean }) => ({
                marginRight: spacing.md,
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.md,
                backgroundColor: hovered ? colors.gray[100] : 'transparent',
                border: `1px solid ${colors.gray[300]}`,
                borderRadius: 6,
                cursor: 'pointer',
              })}
            >
              <CustomText style={{
                color: colors.gray[700],
                fontSize: isMobile ? 16 : 13,
                fontWeight: '600',
                fontFamily: typography.fontFamily.body,
              }}>
                Marketing kit
              </CustomText>
            </Pressable>
            {/* Preview-as-fresh-user (MONEY 86babnkpf): reset this browser's quiz
                state and open the live quiz so Nina/Halle can walk the full flow. */}
            <Pressable
              onPress={handlePreviewAsFreshUser}
              style={({ hovered }: { hovered: boolean }) => ({
                marginRight: spacing.md,
                paddingVertical: spacing.sm,
                paddingHorizontal: spacing.md,
                backgroundColor: hovered ? colors.gray[100] : 'transparent',
                border: `1px solid ${colors.gray[300]}`,
                borderRadius: 6,
                cursor: 'pointer',
              })}
            >
              <CustomText style={{
                color: colors.gray[700],
                fontSize: isMobile ? 16 : 13,
                fontWeight: '600',
                fontFamily: typography.fontFamily.body,
              }}>
                Preview quiz as new user
              </CustomText>
            </Pressable>
            <CustomText style={styles.userEmail}>{user?.email}</CustomText>
            <Pressable
              onPress={() => logout()}
              style={({ hovered }: { hovered: boolean }) => ({
                marginLeft: spacing.md,
                padding: spacing.sm,
                backgroundColor: hovered ? colors.gray[100] : 'transparent',
                borderRadius: 6,
                cursor: 'pointer',
              })}
            >
              <CustomText style={{
                color: colors.gray[700],
                fontSize: isMobile ? 18 : 14,
                fontFamily: typography.fontFamily.body,
              }}>
                Logout
              </CustomText>
            </Pressable>
          </View>
        </View>
      </View>

      {/* Main Content */}
      <View style={isMobile ? { ...styles.content, padding: spacing.md } : styles.content}>
        {activeTab === 'clients' ? (
          <>
            <View style={isMobile
              ? { ...styles.contentHeader, flexDirection: 'column', alignItems: 'stretch', gap: spacing.sm }
              : styles.contentHeader}>
              <CustomText style={styles.title}>Clients</CustomText>
              <View style={isMobile ? { ...styles.searchContainer, width: '100%' } : styles.searchContainer}>
                <View style={styles.searchIconContainer}>
                  <SvgSearch width={16} height={16} color={colors.gray[400]} />
                </View>
                <TextInput
                  placeholder="Search clients, securities and models"
                  style={styles.searchInput}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholderTextColor={colors.gray[400]}
                />
              </View>
            </View>
            
            {/* Loading State */}
            {isLoading && (
              <CustomText style={styles.statusMessage}>Loading clients...</CustomText>
            )}

            {/* Error State */}
            {error && (
              <CustomText style={styles.errorMessage}>
                Error loading clients. Please try again later.
              </CustomText>
            )}
            
            {/* Client List */}
            <View style={isMobile ? { ...styles.clientList, gridTemplateColumns: '1fr', gap: spacing.sm } : styles.clientList}>
              {filteredAccounts?.map((account: Account) => (
                <Pressable 
                  key={account.Id}
                  style={({ hovered }: { hovered: boolean }) => ({
                    ...styles.clientCard,
                    transform: hovered ? 'translateY(-1px)' : 'none',
                    transition: 'all 0.2s ease',
                  })}
                  onPress={() => navigate(`/advisor/client/${account.Id}`)}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <ClientInitials name={account.Name} />
                    <CustomText style={styles.clientName}>{account.Name}</CustomText>
                  </View>
                </Pressable>
              ))}
              {filteredAccounts?.length === 0 && searchQuery && (
                <CustomText style={styles.statusMessage}>No clients found matching your search.</CustomText>
              )}
            </View>
          </>
        ) : (
          <>
            {/* Metrics Row */}
            <View style={styles.metricsRow}>
              <View style={styles.metricCard}>
                <CustomText style={styles.metricLabel}>Leads last 7 days</CustomText>
                <CustomText style={styles.metricValue}>
                  {leadsData ? leadsData.filter((lead: Lead) => {
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                    return new Date(lead.created_at) >= sevenDaysAgo;
                  }).length : 0}
                </CustomText>
              </View>
              
              {isPlatformAdmin && (<>
              <View style={styles.metricCard}>
                <CustomText style={styles.metricLabel}>Subscriptions last 7 days</CustomText>
                <CustomText style={styles.metricValue}>
                  {subscriptionsData ? subscriptionsData.filter((subscription: Subscription) => {
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                    return new Date(subscription.created_at) >= sevenDaysAgo;
                  }).length : 0}
                </CustomText>
              </View>
              
              <View style={styles.metricCard}>
                <CustomText style={styles.metricLabel}>Monthly Recurring Revenue (MRR)</CustomText>
                <CustomText style={styles.metricValue}>
                  {subscriptionsLoading ? (
                    <View style={styles.spinner} />
                  ) : (
                    `$${subscriptionsData ? (subscriptionsData.length * 7.99).toFixed(2) : '0.00'}`
                  )}
                </CustomText>
              </View>
              </>)}
              
              {/* View Toggle Buttons */}
              <View style={styles.viewToggleContainer}>
                <Pressable
                  style={({ hovered }: { hovered: boolean }) => ({
                    ...styles.viewToggleButton,
                    backgroundColor: adminView === 'leads' ? colors.primary : hovered ? colors.gray[100] : colors.white,
                    borderColor: adminView === 'leads' ? colors.primary : colors.gray[300],
                  })}
                  onPress={() => setAdminView('leads')}
                >
                  <CustomText style={{
                    ...styles.viewToggleText,
                    color: adminView === 'leads' ? colors.white : colors.gray[700],
                  }}>
                    Leads
                  </CustomText>
                </Pressable>
                
                {isPlatformAdmin && (
                <Pressable
                  style={({ hovered }: { hovered: boolean }) => ({
                    ...styles.viewToggleButton,
                    backgroundColor: adminView === 'subscriptions' ? colors.primary : hovered ? colors.gray[100] : colors.white,
                    borderColor: adminView === 'subscriptions' ? colors.primary : colors.gray[300],
                  })}
                  onPress={() => setAdminView('subscriptions')}
                >
                  <CustomText style={{
                    ...styles.viewToggleText,
                    color: adminView === 'subscriptions' ? colors.white : colors.gray[700],
                  }}>
                    Subscriptions
                  </CustomText>
                </Pressable>
                )}
                
                {/* Download Leads Button */}
                {adminView === 'leads' && (
                  <Pressable
                    style={({ hovered }: { hovered: boolean }) => ({
                      ...styles.downloadButton,
                      backgroundColor: hovered ? '#22c55e' : colors.success,
                    })}
                    onPress={() => {
                      if (displayedLeads.length) {
                        downloadLeadsCSV(displayedLeads);
                      }
                    }}
                  >
                    <CustomText style={styles.downloadButtonText}>
                      Download Leads
                    </CustomText>
                  </Pressable>
                )}
              </View>
            </View>

            {/* Lead-quality filters (MONEY-7) */}
            {adminView === 'leads' && (
              <View style={styles.filterRow}>
                <View style={styles.filterField}>
                  <CustomText style={styles.filterLabel}>Archetype</CustomText>
                  <select
                    value={archetypeFilter}
                    onChange={(e) => setArchetypeFilter(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">All archetypes</option>
                    {ARCHETYPES.map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </View>
                <View style={styles.filterField}>
                  <CustomText style={styles.filterLabel}>Net worth</CustomText>
                  <select
                    value={netWorthFilter}
                    onChange={(e) => setNetWorthFilter(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">All net worth tiers</option>
                    {netWorthOptions.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </View>
                {(archetypeFilter || netWorthFilter) && (
                  <Pressable
                    style={({ hovered }: { hovered: boolean }) => ({
                      ...styles.clearFilterButton,
                      backgroundColor: hovered ? colors.gray[100] : colors.white,
                    })}
                    onPress={() => { setArchetypeFilter(''); setNetWorthFilter(''); }}
                  >
                    <CustomText style={styles.clearFilterText}>Clear filters</CustomText>
                  </Pressable>
                )}
                <CustomText style={styles.filterCount}>
                  {displayedLeads.length} {displayedLeads.length === 1 ? 'lead' : 'leads'}
                </CustomText>
              </View>
            )}
            
            {/* Error State */}
            {(leadsError || subscriptionsError) && (
              <CustomText style={styles.errorMessage}>
                Error loading data. Please try again later.
              </CustomText>
            )}
            
            {/* Conditional Table Rendering */}
            {adminView === 'leads' ? (
              <>
                {/* Leads: cards on a phone, table otherwise */}
                {isMobile && displayedLeads.length > 0 && (
                  <View>
                    {displayedLeads.map((lead: LeadDetailed) => (
                      <MobileRecordCard
                        key={lead.id}
                        title={`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'N/A'}
                        onPress={() => handleLeadClick(lead)}
                        rows={[
                          { label: 'Email', value: lead.email || 'N/A' },
                          { label: 'Phone', value: lead.phone || 'N/A' },
                          { label: 'Archetype', value: lead.archetype || 'N/A' },
                          ...DEMOGRAPHIC_COLUMNS.map(c => ({ label: c.label, value: infoVal(lead, c.key) || 'N/A' })),
                          { label: 'Created', value: formatLeadDate(lead.created_at) },
                        ]}
                      />
                    ))}
                  </View>
                )}
                {!isMobile && displayedLeads.length > 0 && (
                  <View style={styles.tableContainer}>
                    <View style={styles.tableHeader}>
                      <Pressable style={styles.tableHeaderCell} onPress={() => toggleSort('name')}>
                        <CustomText style={styles.tableHeaderText}>
                          Name{sortKey === 'name' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </CustomText>
                      </Pressable>
                      <CustomText style={styles.tableHeaderCell}>Email</CustomText>
                      <CustomText style={styles.tableHeaderCell}>Phone</CustomText>
                      <Pressable style={styles.tableHeaderCell} onPress={() => toggleSort('archetype')}>
                        <CustomText style={styles.tableHeaderText}>
                          Archetype{sortKey === 'archetype' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </CustomText>
                      </Pressable>
                      {DEMOGRAPHIC_COLUMNS.map(c => (
                        <CustomText key={c.key} style={styles.tableHeaderCell}>{c.label}</CustomText>
                      ))}
                      <Pressable style={styles.tableHeaderCell} onPress={() => toggleSort('created_at')}>
                        <CustomText style={styles.tableHeaderText}>
                          Created{sortKey === 'created_at' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </CustomText>
                      </Pressable>
                    </View>
                    <View style={styles.tableBody}>
                      {displayedLeads.map((lead: LeadDetailed) => (
                        <Pressable
                          key={lead.id}
                          style={({ hovered }: { hovered: boolean }) => ({
                            ...styles.tableRow,
                            backgroundColor: hovered ? colors.gray[100] : 'transparent',
                            cursor: 'pointer',
                          })}
                          onPress={() => handleLeadClick(lead)}
                        >
                          <CustomText style={styles.tableCell}>
                            {lead.first_name} {lead.last_name}
                          </CustomText>
                          <CustomText style={styles.tableCell}>{lead.email}</CustomText>
                          <CustomText style={styles.tableCell}>{lead.phone || 'N/A'}</CustomText>
                          <CustomText style={styles.tableCell}>{lead.archetype || 'N/A'}</CustomText>
                          {DEMOGRAPHIC_COLUMNS.map(c => (
                            <CustomText key={c.key} style={styles.tableCell}>
                              {infoVal(lead, c.key) || 'N/A'}
                            </CustomText>
                          ))}
                          <CustomText style={styles.tableCell}>
                            {new Date(lead.created_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'numeric',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'America/Los_Angeles'
                            })}
                          </CustomText>
                        </Pressable>
                      ))}
                    </View>
                  </View>
                )}

                {leadsData && displayedLeads.length === 0 && (
                  <CustomText style={styles.statusMessage}>
                    {(archetypeFilter || netWorthFilter) ? 'No leads match the current filters.' : 'No leads found.'}
                  </CustomText>
                )}
              </>
            ) : (
              <>
                {/* Subscriptions: cards on a phone, table otherwise */}
                {isMobile && subscriptionsData && subscriptionsData.length > 0 && (
                  <View>
                    {[...subscriptionsData]
                      .sort((a: Subscription, b: Subscription) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                      .map((subscription: Subscription) => (
                        <MobileRecordCard
                          key={subscription.id}
                          title={subscription.lead_details ? `${subscription.lead_details.first_name || ''} ${subscription.lead_details.last_name || ''}`.trim() || 'N/A' : 'N/A'}
                          rows={[
                            { label: 'Email', value: subscription.lead_details?.email || subscription.email || 'N/A' },
                            { label: 'Status', value: String(getSubscriptionStatus(subscription)) },
                            { label: 'Started', value: formatLeadDate(subscription.created_at) },
                            { label: 'Period end', value: subscription.current_period_end ? formatLeadDate(subscription.current_period_end) : 'N/A' },
                          ]}
                        />
                      ))}
                  </View>
                )}
                {!isMobile && subscriptionsData && subscriptionsData.length > 0 && (
                  <View style={styles.tableContainer}>
                    <View style={styles.tableHeader}>
                      <CustomText style={styles.tableHeaderCell}>Name</CustomText>
                      <CustomText style={styles.tableHeaderCell}>Email</CustomText>
                      <CustomText style={styles.tableHeaderCell}>Subscription Date</CustomText>
                      <CustomText style={styles.tableHeaderCell}>Status</CustomText>
                      <CustomText style={styles.tableHeaderCell}>Period End</CustomText>
                      <CustomText style={styles.tableHeaderCell}>Subscription ID</CustomText>
                    </View>
                    <View style={styles.tableBody}>
                      {subscriptionsData
                        .sort((a: Subscription, b: Subscription) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                        .map((subscription: Subscription) => (
                        <View key={subscription.id} style={styles.tableRow}>
                          <CustomText style={styles.tableCell}>
                            {subscription.lead_details ? `${subscription.lead_details.first_name || ''} ${subscription.lead_details.last_name || ''}`.trim() || 'N/A' : 'N/A'}
                          </CustomText>
                          <CustomText style={styles.tableCell}>
                            {subscription.lead_details?.email || subscription.email || 'N/A'}
                          </CustomText>
                          <CustomText style={styles.tableCell}>
                            {(() => {
                              const date = new Date(subscription.created_at);
                              date.setHours(date.getHours() - 7);
                              return date.toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            })()}
                          </CustomText>
                          <CustomText style={styles.tableCell}>
                            {getSubscriptionStatus(subscription)}
                          </CustomText>
                          <CustomText style={styles.tableCell}>
                            {subscription.current_period_end ? (() => {
                              const date = new Date(subscription.current_period_end);
                              date.setHours(date.getHours() - 7);
                              return date.toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            })() : 'N/A'}
                          </CustomText>
                          <CustomText style={styles.tableCell}>
                            {subscription.subscription_id}
                          </CustomText>
                        </View>
                      ))}
                    </View>
                  </View>
                )}
                
                {subscriptionsData && subscriptionsData.length === 0 && (
                  <CustomText style={styles.statusMessage}>No subscriptions found.</CustomText>
                )}
              </>
            )}
          </>
        )}
      </View>

      {/* Lead Quiz Results Modal */}
      <LeadQuizResultsModal 
        lead={selectedLead}
        isVisible={showLeadModal}
        onClose={handleModalClose}
        onDelete={handleLeadDelete}
      />
    </View>
  );
}

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.gray[100],
    minHeight: '100vh',
    margin: '0 auto'
  },
  header: {
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.gray[200]}`,
    width: '100%',
    margin: '0 auto'
  },
  headerContent: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userEmail: {
    color: colors.gray[700],
    fontSize: 14,
    fontFamily: typography.fontFamily.body,
  },
  content: {
    padding: spacing.xl,
    width: '100%',
  },
  contentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.dark,
    fontFamily: typography.fontFamily.heading,
  },
  searchContainer: {
    width: 300,
    position: 'relative',
  },
  searchIconContainer: {
    position: 'absolute',
    left: spacing.sm,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    pointerEvents: 'none',
  },
  searchInput: {
    height: 40,
    borderWidth: 1,
    borderColor: colors.gray[200],
    borderRadius: 6,
    paddingLeft: 36,
    paddingRight: 12,
    outlineStyle: 'none',
    width: '100%',
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    backgroundColor: colors.white,
    color: colors.dark,
  },
  clientList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.lg,
  },
  clientCard: {
    backgroundColor: colors.white,
    borderRadius: 8,
    padding: spacing.lg,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: `1px solid ${colors.gray[200]}`,
  } as React.CSSProperties,
  clientName: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.dark,
    fontFamily: typography.fontFamily.heading,
  },
  statusMessage: {
    fontSize: 16,
    color: colors.gray[600],
    textAlign: 'center',
    marginTop: spacing.xl,
    fontFamily: typography.fontFamily.body,
  },
  errorMessage: {
    fontSize: 16,
    color: colors.danger,
    textAlign: 'center',
    marginTop: spacing.xl,
    fontFamily: typography.fontFamily.body,
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: 8,
    border: `1px solid ${colors.gray[200]}`,
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[200]}`,
    flexShrink: 0,
  },
  tableHeaderCell: {
    flex: 1,
    padding: spacing.md,
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray[700],
    fontFamily: typography.fontFamily.body,
    textAlign: 'left',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray[700],
    fontFamily: typography.fontFamily.body,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.md,
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
  },
  filterField: {
    flexDirection: 'column',
    gap: spacing.xs,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.gray[600],
    fontFamily: typography.fontFamily.body,
    marginBottom: 4,
  },
  clearFilterButton: {
    height: 36,
    paddingHorizontal: spacing.md,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.gray[300],
    cursor: 'pointer',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  clearFilterText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray[700],
    fontFamily: typography.fontFamily.body,
  },
  filterCount: {
    fontSize: 14,
    color: colors.gray[600],
    fontFamily: typography.fontFamily.body,
    marginLeft: 'auto',
    alignSelf: 'center',
  },
  tableBody: {
    flex: 1,
    overflowY: 'auto',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: `1px solid ${colors.gray[100]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tableCell: {
    flex: 1,
    padding: spacing.md,
    fontSize: 14,
    color: colors.dark,
    fontFamily: typography.fontFamily.body,
    textAlign: 'left',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metricsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginBottom: spacing.xl,
    alignItems: 'center',
  },
  metricCard: {
    backgroundColor: colors.white,
    borderRadius: 8,
    padding: spacing.lg,
    border: `1px solid ${colors.gray[200]}`,
    minWidth: 200,
  },
  metricLabel: {
    fontSize: 14,
    color: colors.gray[600],
    fontFamily: typography.fontFamily.body,
    marginBottom: spacing.sm,
  },
  metricValue: {
    fontSize: 32,
    fontWeight: '600',
    color: colors.dark,
    fontFamily: typography.fontFamily.heading,
  },
  spinner: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: colors.gray[300],
    borderTopColor: colors.primary,
    animation: 'spin 1s linear infinite',
  },
  viewToggleContainer: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginLeft: 'auto',
  },
  viewToggleButton: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 6,
    borderWidth: 1,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  viewToggleText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: typography.fontFamily.body,
  },
  downloadButton: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.success,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  downloadButtonText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: typography.fontFamily.body,
    color: colors.white,
  },
} as const; 